// src/pages/CombinedLoginPage.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { sendResetEmail, friendlyAuthError } from '../utils/authEmail';
import { useAuth } from '../context/AuthContext';
import { pathAfterSignIn } from '../utils/googleAuth';
import PasswordInput from '../components/PasswordInput';
import GoogleSignInButton from '../components/GoogleSignInButton';
import BrandName from '../components/BrandName';
import Seo from '../components/Seo';
import { SIMPLE_PAGES } from '../data/simplePages';
import { webPageJsonLd } from '../data/pageSchema';
import useGoogleAuthNavigation from '../hooks/useGoogleAuthNavigation';
import './AuthPages.css';

const seo = SIMPLE_PAGES.find((page) => page.path === '/login');

const CombinedLoginPage = () => {
  const [email, setEmail]             = useState('');
  const [password, setPassword]       = useState('');
  const [loading, setLoading]         = useState(false);
  const [showReset, setShowReset]     = useState(false);
  const [resetEmail, setResetEmail]   = useState('');
  const [resetMsg, setResetMsg]       = useState('');
  const [resetError, setResetError]   = useState('');
  const [resetSending, setResetSending] = useState(false);
  const [resetCooldown, setResetCooldown] = useState(0);
  const [googleLoading, setGoogleLoading] = useState(false);
  const { login, error, signInWithGoogle, pendingSetup } = useAuth();
  const navigate                      = useNavigate();
  useGoogleAuthNavigation('login');

  // A Google account with no users/{uid} doc still has to choose customer or carrier.
  useEffect(() => {
    if (pendingSetup) navigate('/signup', { replace: true });
  }, [pendingSetup, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const result = await login(email, password);
    if (result.success) {
      setEmail('');
      setPassword('');
      navigate(pathAfterSignIn(result), { replace: true });
    }
    setLoading(false);
  };

  const handleGoogle = async () => {
    setGoogleLoading(true);
    await signInWithGoogle();
    setGoogleLoading(false);
  };

  // Reset email cooldown countdown
  useEffect(() => {
    if (resetCooldown <= 0) return undefined;
    const t = setTimeout(() => setResetCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resetCooldown]);

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    if (resetCooldown > 0) return;
    setResetMsg('');
    setResetError('');
    setResetSending(true);
    try {
      await sendResetEmail(resetEmail.trim());
      // Neutral wording: Firebase does not reveal whether an account exists.
      setResetMsg('If an account exists for that email, a reset link is on its way — check your spam/junk folder too.');
      setResetCooldown(60);
    } catch (err) {
      console.error('[ForgotPassword] reset failed:', err.code, err.message);
      setResetError(friendlyAuthError(err));
      if (err.code === 'auth/too-many-requests') setResetCooldown(60);
    }
    setResetSending(false);
  };

  return (
    <div className="auth-page">
      <Seo title={seo.title} description={seo.description} path={seo.path} jsonLd={webPageJsonLd(seo)} />
      <div className="auth-card">
        <div className="auth-logo"><BrandName /></div>
        <h2>Welcome Back</h2>
        <p className="auth-sub">Log in to your account</p>
        {error && <div className="auth-error">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input className="form-input" type="email" placeholder="you@example.com"
              value={email} onChange={e => setEmail(e.target.value)} required />
          </div>
          <div className="form-group">
            <label className="form-label">Password</label>
            <PasswordInput className="form-input" placeholder="••••••••"
              value={password} onChange={e => setPassword(e.target.value)} required autoComplete="current-password" />
            <button
              type="button"
              className="forgot-link"
              onClick={() => { setShowReset(!showReset); setResetMsg(''); setResetError(''); }}
            >
              Forgot password?
            </button>
          </div>
          <button type="submit" className="btn btn-primary btn-full" disabled={loading || googleLoading}>
            {loading ? 'LOGGING IN...' : 'LOGIN'}
          </button>
          <GoogleSignInButton
            label="Sign in with Google"
            onClick={handleGoogle}
            disabled={loading}
            busy={googleLoading}
          />
        </form>

        {showReset && (
          <div className="reset-panel">
            <p className="reset-intro">Enter your email and we'll send a reset link.</p>
            {resetMsg   && <div className="reset-success">{resetMsg}</div>}
            {resetError && <div className="auth-error">{resetError}</div>}
            <form onSubmit={handleResetSubmit}>
              <div className="form-group">
                <label className="form-label">Your Email</label>
                <input
                  className="form-input"
                  type="email"
                  placeholder="you@example.com"
                  value={resetEmail}
                  onChange={e => setResetEmail(e.target.value)}
                  required
                />
              </div>
              <button type="submit" className="btn btn-secondary btn-full" disabled={resetSending || resetCooldown > 0}>
                {resetSending ? 'SENDING...' : resetCooldown > 0 ? `SEND AGAIN IN ${resetCooldown}s` : 'SEND RESET EMAIL'}
              </button>
            </form>
          </div>
        )}

        <p className="auth-footer" style={{marginTop:'16px'}}>
          Don't have an account? <Link to="/signup">Sign up free</Link>
        </p>
      </div>
    </div>
  );
};

export default CombinedLoginPage;
